import React, { useState } from 'react';
import {
    Container,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    TablePagination,
    TextField,

} from '@mui/material';
import axios from 'axios';
import Page from '../components/Page'; 
import SearchNotFound from '../components/SearchNotFound'; 

const CompanySelector = () => {
    // Hardcoded categories
    const categories = [
        'INDIVIDUAL',
        'LIFE MEMBER',
        'SENIOR CITIZEN',
        'STUDENT',
        'INDUSTRY',
        'INSTITUTION',
        'TRADE UNION',
        'ASSOCIATION'
    ];

    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(false); // Loading state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [filterName, setFilterName] = useState('');
    const [category, setCategory] = useState('INDIVIDUAL'); 

    // Fetch companies based on selected category
    const fetchCompanies = async (category) => {
        setLoading(true); // Set loading to true
        try {
            const response = await axios.get(`https://form.cpccbe.org/api/stats/get-company`);
           
            console.log("Fetched Companies:", response.data); // Log the fetched data
            
            let companiesList = [];

            switch (category) {
                case 'INDIVIDUAL':
                    companiesList = response.data.individuals.map(ind => ({
                        companyname: ind.companyName || '-',
                        name : ind.name || '-',
                        email: ind.email || '-',
                        linkedInId: ind.linkedInId || '-',
                        websiteLink: ind.websiteLink || '-',
                        telephoneOffice: ind.phoneNumber || '-',
                    }));
                    break;
                case 'LIFE MEMBER':
                    companiesList = response.data.lifeMembers.map(life => ({
                        companyname: life.organization.name || '-',
                        name:   life.name || '-',   
                        email: life.email || '-',
                        linkedInId: life.linkedInId || '-',
                        websiteLink: life.websiteLink || '-',
                        telephoneOffice: life.phoneNumber || '-',
                    }));
                    break;
                case 'SENIOR CITIZEN':
                    companiesList = response.data.seniorCitizens.map(senior => ({
                        companyname: senior.organization.name || '-',
                        name: senior.name || '-',
                        email: senior.email || '-',
                        linkedInId: senior.linkedInId || '-',
                        websiteLink: senior.websiteLink || '-',
                        telephoneOffice: senior.phoneNumber || '-',
                    }));
                    break;
                case 'STUDENT':
                    companiesList = response.data.students.map(student => ({
                        organizationname: student.organization.name,
                        name: student.name || '-',
                        email: student.email || '-',
                        linkedInId: student.linkedInId || '-',
                        websiteLink: student.websiteLink || '-',
                        telephoneOffice: student.phoneNumber || '-',
                    }));
                    break;
                case 'INDUSTRY':
                    companiesList = response.data.industries.map(industry => ({
                        companyname: industry.organizationName,
                        name : industry.nameofrepresentative,
                        email: industry.email || '-',
                        linkedInId: industry.linkedin || '-',
                        websiteLink: industry.website || '-',
                        telephoneOffice: industry.telephoneOffice || '-',
                        telephoneFactory: industry.telephoneFactory || '-',
                    }));
                    break;
                case 'INSTITUTION':
                    companiesList = response.data.institutions.map(institution => ({
                        companyname: institution.organizationName || '-',
                        name : institution.nameofrepresentative || '-',
                        email: institution.email || '-',
                        linkedInId: institution.linkedin || '-',
                        websiteLink: institution.website || '-',
                        telephoneOffice: institution.telephoneOffice || '-',
                    }));
                    break;
                case 'TRADE UNION':
                    companiesList = response.data.tradeUnions.map(tradeUnion => ({
                        companyname: tradeUnion.organizationName || '-',
                        name: tradeUnion.nameofrepresentative || '-',
                        email: tradeUnion.email || '-',
                        linkedInId: tradeUnion.linkedin || '-',
                        websiteLink: tradeUnion.website || '-',
                        telephoneOffice: tradeUnion.telephoneOffice || '-',
                    }));
                    break;
                case 'ASSOCIATION':
                    companiesList = response.data.associations.map(association => ({
                        companyname: association.organizationName || '-',
                        name: association.nameofrepresentative || '-',
                        email: association.email || '-',
                        linkedInId: association.linkedin || '-',
                        websiteLink: association.website || '-',
                        telephoneOffice: association.telephoneOffice || '-',
                    }));
                    break;
                default:
                    console.error("Invalid category provided");
                    return; // Exit if category is invalid
            }

            // Set the extracted companies
            setCompanies(companiesList);
        } catch (error) {
            console.error("Error fetching companies:", error);
            setCompanies([]); // Reset to empty array on error to avoid map issues
        } finally {
            setLoading(false); // Set loading to false after fetching
        }
    };

    const handleCategoryClick = (category) => {
        setCategory(category);
        fetchCompanies(category);
    };
    

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleFilterByName = (event) => {
        setFilterName(event.target.value);
    };

    // Apply filtering
    const filteredCompanies = companies.filter((company) =>
        (company.name || company.companyname || '').toLowerCase().includes(filterName.toLowerCase())
    );

    const isUserNotFound = filteredCompanies.length === 0;

    const getColumns = (category) => {
        switch (category) {
            case 'INDUSTRY':
                return ['companyname', 'name', 'email', 'linkedInId', 'websiteLink', 'telephoneOffice', 'telephoneFactory'];
            case 'STUDENT':
                return ['organizationname', 'name', 'email', 'linkedInId', 'websiteLink', 'telephoneOffice'];
            default:
                return ['companyname', 'name', 'email', 'linkedInId', 'websiteLink', 'telephoneOffice'];
        }
    };
    
    const columns = getColumns(category);
    
    return (
        <Page title="Company List">
        <Container>
            <Typography variant="h4" gutterBottom>
                Select a Category
            </Typography>
            <div>
                {categories.map((cat) => (
                    <Button key={cat} onClick={() => handleCategoryClick(cat)} sx={{ marginRight: 1 }}>
                        {cat}
                    </Button>
                ))}
            </div>

            {loading && <CircularProgress sx={{ marginTop: 2 }} />}

            {!loading && (
                <>
                    <TextField 
                      label="Search by Name"
                      variant="outlined"
                      value={filterName}
                      onChange={handleFilterByName}
                      sx={{ marginTop: 2, marginBottom: 2 }}
                      fullWidth
                    />
                    
                    <TableContainer sx={{ marginTop: 2 }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    {columns.map((column) => (
                                        <TableCell key={column}>{column}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredCompanies
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((company, index) => (
                                        <TableRow key={index}>
                                            {columns.map((column) => (
                                                <TableCell key={column}>{company[column] || '-'}</TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                  {/* Display Search Not Found */}
                  {isUserNotFound && (
                      <Typography variant="body2" sx={{ mt: 2 }}>
                          No companies found for this category.
                      </Typography>
                  )}
                  
                  {/* Pagination */}
                  <TablePagination
                      rowsPerPageOptions={[5, 10, 25]}
                      component="div"
                      count={filteredCompanies.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                  
                </>
            )}
        </Container>
        </Page>
    );
};

export default CompanySelector;
